import React, { useState } from "react";
import { usePayloadSchema } from "@/hooks/usePayloads";
import { PayloadReadOnlyView } from "./PayloadReadOnlyView";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Settings, Loader2, Lock } from "lucide-react";

interface PayloadDetailCardProps {
  payload: {
    policyPayloadId?: string;
    kind?: string;
    identifier?: string;
    title?: string;
    configJson?: string;
  };
}

function parseConfigJson(configJson: string | undefined): Record<string, any> {
  if (!configJson) return {};
  if (typeof configJson === "object") return configJson as Record<string, any>;
  try {
    return JSON.parse(configJson);
  } catch {
    return {};
  }
}

export function PayloadDetailCard({ payload }: PayloadDetailCardProps) {
  const [open, setOpen] = useState(false);
  const config = parseConfigJson(payload.configJson);
  const configKeys = Object.keys(config);

  const { data: schemaData, isLoading: schemaLoading, error: schemaError } = usePayloadSchema(
    payload.kind || "",
    payload.identifier || "",
    open
  );

  const schema = (schemaData as any)?.jsonSchema || (schemaData as any)?.schema || schemaData;
  const usableSchema =
    schema && schema.type === "object" && schema.properties ? schema : null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full text-left p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors group">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Settings className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-sm">
                  {payload.title || payload.kind || "Untitled Payload"}
                </h4>
                {payload.kind && (
                  <Badge variant="outline" className="text-xs capitalize">
                    {payload.kind}
                  </Badge>
                )}
                {configKeys.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {configKeys.length} setting{configKeys.length !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
              {payload.identifier && (
                <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                  {payload.identifier}
                </p>
              )}
            </div>
            <div className="shrink-0 text-muted-foreground group-hover:text-foreground transition-colors">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
        <div className="px-4 pb-4 pt-2 ml-[18px] border-l-2 border-muted space-y-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" aria-hidden />
            <span>Read-only — edit the profile to change these settings</span>
          </div>
          {schemaLoading ? (
            <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading schema…</span>
            </div>
          ) : (
            <PayloadReadOnlyView
              schema={usableSchema}
              values={config}
              schemaError={schemaError}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
